import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { playDing, primeAudio } from "@/lib/sound";
import Inbox from "@/components/Inbox";
import MassSms from "@/components/MassSms";
import Customers from "@/components/Customers";
import Settings from "@/components/Settings";
import { Button } from "@/components/ui/button";
import {
  ChatCircleDots,
  ChatCircle,
  Megaphone,
  UsersThree,
  GearSix,
  SignOut,
  SpeakerHigh,
  SpeakerSlash,
} from "@phosphor-icons/react";

const NAV = [
  { key: "inbox", label: "Inbox", icon: ChatCircle },
  { key: "mass", label: "Mass SMS", icon: Megaphone },
  { key: "customers", label: "Customers", icon: UsersThree },
  { key: "settings", label: "Settings", icon: GearSix },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("inbox");
  const [conversations, setConversations] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const lastInboundCount = useRef(null);

  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data.conversations);
      setTotalUnread(data.total_unread);

      const inboundTotal = data.conversations.reduce(
        (sum, c) =>
          sum +
          (c.last_message?.direction === "inbound" ? c.unread : 0) +
          c.unread,
        0
      );
      // Detect a fresh inbound arrival by comparing unread totals.
      if (lastInboundCount.current !== null && data.total_unread > lastInboundCount.current) {
        if (soundOnRef.current) playDing();
      }
      lastInboundCount.current = data.total_unread;
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadConversations();
    const id = setInterval(loadConversations, 5000);
    return () => clearInterval(id);
  }, [loadConversations]);

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="h-screen flex bg-background text-foreground overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border flex flex-col bg-card/40">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <div className="h-9 w-9 bg-primary flex items-center justify-center rounded-sm">
            <ChatCircleDots size={20} weight="fill" className="text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <div className="font-semibold tracking-tight">TxtSync</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              SMS Console
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = view === item.key;
            return (
              <button
                key={item.key}
                data-testid={`nav-${item.key}`}
                onClick={() => {
                  primeAudio();
                  setView(item.key);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-100 ${
                  active
                    ? "bg-primary/15 text-primary border border-primary/40"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                <span className="flex-1 text-left">{item.label}</span>
                {item.key === "inbox" && totalUnread > 0 && (
                  <span
                    data-testid="inbox-unread-badge"
                    className="text-[10px] font-semibold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm min-w-[18px] text-center"
                  >
                    {totalUnread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-border space-y-3">
          <button
            data-testid="toggle-sound"
            onClick={() => {
              primeAudio();
              setSoundOn((s) => !s);
            }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {soundOn ? <SpeakerHigh size={18} /> : <SpeakerSlash size={18} />}
            <span>Sound {soundOn ? "on" : "off"}</span>
          </button>
          <div className="flex items-center gap-3 px-2">
            <div className="h-9 w-9 rounded-sm bg-secondary flex items-center justify-center text-xs font-semibold border border-border">
              {initials}
            </div>
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-sm truncate" data-testid="current-user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {user?.role}
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              data-testid="logout-button"
              onClick={logout}
              className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive"
            >
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {view === "inbox" && (
          <Inbox
            conversations={conversations}
            refreshConversations={loadConversations}
          />
        )}
        {view === "mass" && <MassSms />}
        {view === "customers" && <Customers />}
        {view === "settings" && <Settings isAdmin={user?.role === "admin"} />}
      </main>
    </div>
  );
}
